/**
 * Web-side mirror of `shared/src/util/mortgage.ts` — amortization +
 * refinance break-even helpers used by the `/housing` per-unit drilldown's
 * Amortization + Refinance tabs. Web does NOT import from `@hearth/shared`
 * (locked since 5.1; pulls `node:crypto` into the Vite bundle). Behavior
 * must stay in sync with the shared implementation; the matching
 * shared/test/util-mortgage.test.ts cases are the contract.
 */

export interface AmortizeInput {
  readonly principal_cents: number;
  readonly rate_pct_yearly: number;
  readonly months_remaining: number;
  readonly monthly_payment_cents: number;
}

export interface AmortizationRow {
  readonly month: number;
  readonly principal_paid_cents: number;
  readonly interest_paid_cents: number;
  readonly remaining_balance_cents: number;
}

export interface AmortizationSchedule {
  readonly schedule: readonly AmortizationRow[];
  readonly total_interest_cents: number;
  readonly total_principal_paid_cents: number;
}

export function monthlyPayment(
  principal_cents: number,
  rate_pct_yearly: number,
  months: number,
): number {
  if (months <= 0) return 0;
  if (rate_pct_yearly === 0) {
    return Math.round(principal_cents / months);
  }
  const r = rate_pct_yearly / 12 / 100;
  const factor = Math.pow(1 + r, months);
  const payment = principal_cents * ((r * factor) / (factor - 1));
  return Math.round(payment);
}

export function amortize(input: AmortizeInput): AmortizationSchedule {
  const { principal_cents, rate_pct_yearly, months_remaining, monthly_payment_cents } = input;
  if (months_remaining <= 0 || principal_cents <= 0) {
    return {
      schedule: [],
      total_interest_cents: 0,
      total_principal_paid_cents: 0,
    };
  }
  const monthlyRate = rate_pct_yearly / 12 / 100;
  const rows: AmortizationRow[] = [];
  let balance = principal_cents;
  let totalInterest = 0;
  let totalPrincipal = 0;
  for (let m = 1; m <= months_remaining; m += 1) {
    const interestRaw = balance * monthlyRate;
    const interest = Math.round(interestRaw);
    let principalPaid = monthly_payment_cents - interest;
    let nextBalance = balance - principalPaid;
    if (m === months_remaining) {
      principalPaid = balance;
      nextBalance = 0;
    } else if (nextBalance < 0) {
      principalPaid = balance;
      nextBalance = 0;
    }
    rows.push({
      month: m,
      principal_paid_cents: principalPaid,
      interest_paid_cents: interest,
      remaining_balance_cents: nextBalance,
    });
    totalInterest += interest;
    totalPrincipal += principalPaid;
    balance = nextBalance;
    if (balance <= 0 && m < months_remaining) break;
  }
  return {
    schedule: rows,
    total_interest_cents: totalInterest,
    total_principal_paid_cents: totalPrincipal,
  };
}

export interface RefinanceLoanParams {
  readonly principal_cents: number;
  readonly rate_pct_yearly: number;
  readonly months_remaining: number;
  readonly monthly_payment_cents?: number;
}

export interface RefinanceBreakevenInput {
  readonly current_loan: RefinanceLoanParams;
  readonly new_loan: RefinanceLoanParams;
  readonly closing_costs_cents: number;
}

export interface RefinanceBreakevenResult {
  readonly months_to_breakeven: number | null;
  readonly lifetime_interest_savings_cents: number;
  readonly monthly_payment_delta_cents: number;
  readonly current_monthly_payment_cents: number;
  readonly new_monthly_payment_cents: number;
}

export function refinanceBreakeven(
  input: RefinanceBreakevenInput,
): RefinanceBreakevenResult {
  const currentPmt =
    input.current_loan.monthly_payment_cents ??
    monthlyPayment(
      input.current_loan.principal_cents,
      input.current_loan.rate_pct_yearly,
      input.current_loan.months_remaining,
    );
  const newPmt =
    input.new_loan.monthly_payment_cents ??
    monthlyPayment(
      input.new_loan.principal_cents,
      input.new_loan.rate_pct_yearly,
      input.new_loan.months_remaining,
    );
  const monthlyDelta = currentPmt - newPmt;

  const currentSchedule = amortize({
    principal_cents: input.current_loan.principal_cents,
    rate_pct_yearly: input.current_loan.rate_pct_yearly,
    months_remaining: input.current_loan.months_remaining,
    monthly_payment_cents: currentPmt,
  });
  const newSchedule = amortize({
    principal_cents: input.new_loan.principal_cents,
    rate_pct_yearly: input.new_loan.rate_pct_yearly,
    months_remaining: input.new_loan.months_remaining,
    monthly_payment_cents: newPmt,
  });
  const lifetimeInterestSavings =
    currentSchedule.total_interest_cents - newSchedule.total_interest_cents;

  let monthsToBreakeven: number | null = null;
  if (monthlyDelta > 0) {
    monthsToBreakeven = Math.ceil(input.closing_costs_cents / monthlyDelta);
  }

  return {
    months_to_breakeven: monthsToBreakeven,
    lifetime_interest_savings_cents: lifetimeInterestSavings,
    monthly_payment_delta_cents: monthlyDelta,
    current_monthly_payment_cents: currentPmt,
    new_monthly_payment_cents: newPmt,
  };
}
